import {Inject, Injectable} from '@angular/core';
import {BaseService} from './base-service';
import {OrderItem} from '../_models/order-item';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {LibConfig, LibConfigService} from '../lib.config';
import {MenuItem} from '../_models/menu-item';
import {MenuItemPrice} from '../_models/menu-item-price';
import {OrderItemSelection} from '../_models/order-item-selection';
import {OrderItemSelectionItem} from '../_models/order-item-selection-item';
import {OrderItemToppingModifier} from '../_models/order-item-topping-modifier';
import {Availabilities} from '../_constants/availabilities';
import {ModifierDisplayPosition} from '../_constants/modifier-display-position';
import {OrderItemTopping} from '../_models/order-item-topping';
import {OrderItemModifier} from '../_models/order-item-modifier';
import {MenuItemToppingRule} from '../_constants/menu-item-topping-rule';

@Injectable({
    providedIn: 'root'
})
export class OrderItemService extends BaseService<OrderItem> {
    destination = 'order-items';

    private orderItems: Observable<OrderItem[]>;
    private orderItemsSubject: BehaviorSubject<OrderItem[]>;

    constructor(
        http: HttpClient,
        @Inject(LibConfigService) config: LibConfig
    ) {
        super(http, config);
        this.orderItemsSubject = new BehaviorSubject<Array<OrderItem>>(new Array<OrderItem>());
        this.orderItems = this.orderItemsSubject.asObservable();
    }

    getTab(orderItemId) {
        return this.http.get(`${this.baseUrl()}/${orderItemId}/tab`);
    }

    initializeOrderItem(menuItem: MenuItem, updateItem?: OrderItem) {
        const orderItem = new OrderItem(!!updateItem ? updateItem : {});
        orderItem.menuItem = menuItem;

        if (!!menuItem) {
            if (!updateItem) {
                this.addDefaultModifiers(orderItem);
            }

            orderItem.includedToppings = menuItem.toppings ?
                menuItem.toppings.filter((topping) => (topping.included && topping.topping.available))
                    .map(mit => {
                        const orderItemTopping = new OrderItemTopping({});
                        orderItemTopping.menuItemTopping = mit;
                        const updatedTopping = !!updateItem ? updateItem.includedToppings.find(t => t.menuItemTopping.id === mit.id) : null;
                        orderItemTopping.modifiers = !!updatedTopping ? updatedTopping.modifiers : [];
                        return orderItemTopping;
                    }) : [];

            orderItem.includedToppings.forEach((topping) => {
                const found = !!updateItem && !!updateItem.includedToppings
                    ? updateItem.includedToppings.find(t => t.menuItemTopping.id === topping.menuItemTopping.id) : null;
                topping.included = !found || found.included;

                if (!updateItem) {
                    this.addDefaultToppingModifiers(topping);
                }
            });

            orderItem.optionalToppings = menuItem.toppings ?
                menuItem.toppings.filter((topping) => (!topping.included && topping.topping.available))
                    .map(mit => {
                        const orderItemTopping = new OrderItemTopping({});
                        orderItemTopping.menuItemTopping = mit;
                        const updatedTopping = !!updateItem ? updateItem.optionalToppings.find(t => t.menuItemTopping.id === mit.id) : null;
                        orderItemTopping.modifiers = !!updatedTopping ? updatedTopping.modifiers : [];
                        return orderItemTopping;
                    }) : [];

            orderItem.optionalToppings.forEach((topping) => {
                const found = !!updateItem && !!updateItem.optionalToppings
                    ? updateItem.optionalToppings.find(t => t.menuItemTopping.id === topping.menuItemTopping.id) : null;
                topping.included = found && found.included;

                this.recalculateOptionalToppingPrice(topping, orderItem.selectedPrice);

                if (!updateItem) {
                    this.addDefaultToppingModifiers(topping);
                }
            });

            this.distributeOptionalToppingPrices(orderItem.optionalToppings, menuItem);

            if (!updateItem || !updateItem.selectedPrice) {
                const defaultPrice = menuItem.prices && menuItem.prices.filter(price => price.defaultPrice);
                orderItem.selectedPrice = (defaultPrice && defaultPrice.length > 0)
                    ? defaultPrice[0] : (menuItem.prices ? menuItem.prices[0] : new MenuItemPrice());
            } else {
                orderItem.selectedPrice = menuItem.prices.find(p => p.id === updateItem.selectedPrice.id);

                if (orderItem.selectedPrice.variable) {
                    orderItem.selectedPrice.price = orderItem.priceOverride;
                }
            }

            orderItem.selections = [];
            if (menuItem.selections) {
                menuItem.selections.forEach(menuItemSelection => {
                    if (!!updateItem) {
                        const found = updateItem.selections.find(s => s.menuItemSelection.id === menuItemSelection.id);
                        if (!!found) {
                            const selection = new OrderItemSelection(found);
                            selection.selectedItems.forEach(i => {
                                const menuSelectionItem = menuItemSelection.menuSelection.options
                                    .find(opt => opt.menuItem.id === i.orderItem.menuItem.id);
                                i.orderItem = this.initializeOrderItem(menuSelectionItem.menuItem, i.orderItem);
                            });
                            orderItem.selections.push(selection);
                        }
                    } else {
                        const selection = new OrderItemSelection({});
                        selection.menuItemSelection = menuItemSelection;

                        selection.selectedItems = [];
                        menuItemSelection.menuSelection.options.filter(opt => {
                            return opt.defaultSelection && opt.menuItem.availability !== Availabilities.NONE;
                        }).forEach(menuSelectionItem => {
                            const item = new OrderItemSelectionItem({});
                            item.menuSelectionItem = menuSelectionItem;
                            item.orderItem = this.initializeOrderItem(menuSelectionItem.menuItem);
                            selection.selectedItems.push(item);
                        });

                        orderItem.selections.push(selection);
                    }
                });
            }
        }
        orderItem.credits = [];
        return orderItem;
    }

    recalculateOptionalToppingPrice(orderItemTopping: OrderItemTopping, selectedPrice: MenuItemPrice) {
        if (!!orderItemTopping.menuItemTopping.prices
            && orderItemTopping.menuItemTopping.prices.length === 1
            && !orderItemTopping.menuItemTopping.prices[0].menuItemPrice) {
            orderItemTopping.computedPrice = orderItemTopping.menuItemTopping.prices[0].price;
        } else if (!!selectedPrice) {
            const found = orderItemTopping.menuItemTopping.prices
                .find(p => p.menuItemPrice.id === selectedPrice.id);
            if (!!found) {
                orderItemTopping.computedPrice = found.price;
            }
        } else {
            orderItemTopping.computedPrice = 0;
        }
    }

    distributeOptionalToppingPrices(optionalToppings: OrderItemTopping[], menuItem: MenuItem) {
        if (!!optionalToppings && !!menuItem) {
            const temp = optionalToppings.filter(o => o.included);
            temp.sort((a, b) =>
                menuItem.menuItemToppingRule === MenuItemToppingRule.LEAST_EXPENSIVE ?
                    a.computedPrice - b.computedPrice : b.computedPrice - a.computedPrice
            );

            let freeCount = menuItem.freeOptionalToppingCount;
            temp.forEach(o => {
                o.additionalPrice = freeCount-- > 0 ? 0 : o.computedPrice;
            });
        }
    }

    addDefaultToppingModifiers(topping: OrderItemTopping) {
        if (!!topping.menuItemTopping.topping.modifierSelections
            && topping.menuItemTopping.topping.modifierSelections.length > 0) {
            topping.menuItemTopping.topping.modifierSelections.forEach(menuToppingModifierSelection => {
                menuToppingModifierSelection.menuModifierSelection.options
                    .filter(menuModifierSelectionOption => menuModifierSelectionOption.defaultOption)
                    .forEach(defaultMenuModifierSelectionOption => {
                        const toppingOption = menuToppingModifierSelection.options
                            .find(f => f.menuModifier.id === defaultMenuModifierSelectionOption.menuModifier.id);
                        if (!!toppingOption) {
                            topping.modifiers.push(
                                new OrderItemToppingModifier(this.buildModifier(menuToppingModifierSelection, toppingOption)));
                        }
                    });
            });
        }

        if (!!topping.menuItemTopping.topping.modifierGroups
            && topping.menuItemTopping.topping.modifierGroups.length > 0) {
            topping.menuItemTopping.topping.modifierGroups.forEach(menuToppingModifierGroup =>
                menuToppingModifierGroup.menuModifierGroup.selections.forEach(menuModifierGroupSelection => {
                    menuModifierGroupSelection.menuModifierSelection.options.filter(o => o.defaultOption)
                        .forEach(defaultMenuModifierSelectionOption => {
                            const toppingOption = menuModifierGroupSelection.options
                                .find(f => f.menuModifier.id === defaultMenuModifierSelectionOption.menuModifier.id);
                            if (!!toppingOption) {
                                topping.modifiers.push(
                                    new OrderItemToppingModifier(this.buildModifier(menuModifierGroupSelection, toppingOption)));
                            }
                        });
                }));
        }
    }

    addDefaultModifiers(orderItem: OrderItem) {
        if (!!orderItem.menuItem.modifierSelections
            && orderItem.menuItem.modifierSelections.length > 0) {
            orderItem.menuItem.modifierSelections.forEach(menuItemModifierSelection => {
                menuItemModifierSelection.menuModifierSelection.options
                    .filter(menuModifierSelectionOption => menuModifierSelectionOption.defaultOption)
                    .forEach(defaultMenuModifierSelectionOption => {
                        const itemOption = menuItemModifierSelection.options
                            .find(f => f.menuModifier.id === defaultMenuModifierSelectionOption.menuModifier.id);
                        if (!!itemOption) {
                            orderItem.modifiers.push(new OrderItemModifier(this.buildModifier(menuItemModifierSelection, itemOption)));
                        }
                    });
            });
        }

        if (!!orderItem.menuItem.modifierGroups
            && orderItem.menuItem.modifierGroups.length > 0) {
            orderItem.menuItem.modifierGroups.forEach(menuItemModifierGroup =>
                menuItemModifierGroup.menuModifierGroup.selections.forEach(menuModifierGroupSelection => {
                    menuModifierGroupSelection.menuModifierSelection.options.filter(o => o.defaultOption)
                        .forEach(defaultMenuModifierSelectionOption => {
                            const itemOption = menuModifierGroupSelection.options
                                .find(f => f.menuModifier.id === defaultMenuModifierSelectionOption.menuModifier.id);
                            if (!!itemOption) {
                                orderItem.modifiers.push(new OrderItemModifier(this.buildModifier(menuModifierGroupSelection, itemOption)));
                            }
                        });
                }));
        }
    }

    buildModifier(parentDefinition: any, parentSelectionOption: any): any {
        const menuModifierSelection = parentDefinition.menuModifierSelection;
        const selectionOption = menuModifierSelection.options.find(f => f.menuModifier.id === parentSelectionOption.menuModifier.id);
        return {
            additionalPrice: parentSelectionOption.additionalPrice,
            menuModifier: parentSelectionOption.menuModifier,
            groupByModifier: menuModifierSelection.groupByModifier,
            defaultModifier: selectionOption.defaultOption,
            modifierDisplayPosition: !!selectionOption
                ? selectionOption.modifierDisplayPosition
                : ModifierDisplayPosition.STANDARD,
            selectionName: menuModifierSelection.name,
            selectionRequired: menuModifierSelection.required,
            selectionSortOrder: parentDefinition.sortOrder,
            sortOrder: !!selectionOption ? selectionOption.sortOrder : null
        };
    }

    public count(): number {
        return this.orderItemsSubject.getValue().length;
    }

    public getCurrent(): OrderItem {
        const items: OrderItem[] = this.orderItemsSubject.getValue();
        return (items.length > 0) ? items[items.length - 1] : null;
    }

    public removeCurrent() {
        const items: OrderItem[] = this.orderItemsSubject.getValue();
        if (items && items.length > 0) {
            items.pop();
        }
        this.orderItemsSubject.next(items);
    }

    public add(orderItem: OrderItem) {
        const current = this.getCurrent();
        if (current == null || orderItem.menuItem.id !== current.menuItem.id) {
            this.orderItemsSubject.getValue().push(orderItem);
        }
    }

    public clear() {
        this.orderItemsSubject.next(new Array<OrderItem>());
    }
}
